import * as yup from 'yup'
import {
  aggregatorFormFields as AFF,
  DELIVEROO_FIELDS as DF,
  aggParentFields,
  authTypes,
} from 'components/aggregatorManagement/utils'
import { HOST_REGEX, NO_SPECIAL_CHAR, NUMBER_REGEX } from 'utils/constants'
import './customYupMethods'

export const checkLastChar = (word: any) => {
  const lastLetter = word[word.length - 1]
  if (lastLetter === '/' || lastLetter === '\\') {
    return false
  }
  return true
}

export const makeConditionallyRequired = (key: string, message: string) => {
  return yup
    .string()
    .trim()
    .when(key, {
      is: true,
      then: (schema) => schema.required(message),
      otherwise: (schema) => schema,
    })
}

// Aggregator FTP Configuration Schema
const ftpConfigSchema = yup.object().shape({
  [AFF.FTP_ACTIVE.key]: yup.boolean(),
  [AFF.FTP_DELETE_FILES.key]: yup.boolean(),
  [AFF.FTP_ENABLE_S3_STORAGE.key]: yup.boolean(),
  [AFF.FTP_PROTOCOL.key]: yup
    .string()
    .trim()
    .required('FTP Protocol is required field'),
  [AFF.FTP_HOST.key]: yup
    .string()
    .trim()
    .when(AFF.FTP_ACTIVE.key, {
      is: true,
      then: (schema) => schema.required('FTP Host is a required field'),
      otherwise: (schema) => schema,
    })
    .matches(HOST_REGEX, {
      message: 'Please provide valid host',
      excludeEmptyString: true,
    }),
  [AFF.FTP_PORT.key]: yup
    .string()
    .trim()
    .ensure()
    .when(AFF.FTP_ACTIVE.key, {
      is: true,
      then: (schema) => schema.required('FTP Port is required field'),
      otherwise: (schema) => schema,
    })
    .matches(NUMBER_REGEX, {
      message: 'FTP Port must be positive numerical value',
      excludeEmptyString: true,
    }),
  [AFF.FTP_FILE_GENERATION_LIMIT.key]: yup
    .number()
    .required('FTP File Generation Limit is required')
    .typeError('FTP File Generation Limit must be a number')
    .min(1, 'Minimum value allowed is 1')
    .max(20, 'Maximum value allowed is 20'),
  [AFF.FTP_USERNAME.key]: makeConditionallyRequired(
    AFF.FTP_ACTIVE.key,
    'FTP Username is a required field',
  ),
  [AFF.FTP_PASSWORD.key]: makeConditionallyRequired(
    AFF.FTP_ACTIVE.key,
    'FTP Password is a required field',
  ),
  [AFF.FTP_PATH.key]: makeConditionallyRequired(
    AFF.FTP_ACTIVE.key,
    'FTP Path is a required field',
  ),
  [AFF.FTP_CRON_EXPRESSION.key]: makeConditionallyRequired(
    AFF.FTP_ACTIVE.key,
    'FTP Cron Expression is a required field',
  ),
  [AFF.FTP_ACTIVATED_STORES.key]: yup.array().of(yup.string()),
  [AFF.FTP_MAX_RUNTIME_MINUTES.key]: yup
    .number()
    .typeError('Max Runtime must be a number')
    .integer('Max Runtime must be a whole number')
    .min(1, 'Minimum value allowed is 1')
    .max(1440, 'Maximum value allowed is 1440 (1 day)')
    .notRequired(),
  [AFF.FTP_MAX_HOURS_WITHOUT_RUN.key]: yup
    .number()
    .typeError('Max Hours Without Run must be a number')
    .integer('Max Hours Without Run must be a whole number')
    .min(1, 'Minimum value allowed is 1')
    .max(168, 'Maximum value allowed is 168 (7 days)')
    .notRequired(),
})

const validateBasedOnAuth = (type: string, message: string) => {
  return yup
    .string()
    .trim()
    .when([AFF.API_ACTIVE.key, AFF.API_AUTH_TYPE.key], (fields, schema) => {
      if (fields[0] === true && fields[1] === type) {
        return schema.required(message)
      }

      return schema
    })
}

// Auth: Bearer
const bearerAuthFields = {
  [AFF.API_AUTH_URL.key]: validateBasedOnAuth(
    authTypes.BEARER,
    'URL is a required field',
  ),
  [AFF.API_AUTH_REQ_SCHEMA.key]: validateBasedOnAuth(
    authTypes.BEARER,
    'Auth Request Schema is a required field',
  ).isValidJson('Please Provide Valid Request Schema', {
    excludeEmptyString: true,
  }),
  [AFF.API_AUTH_TOKEN_KEY.key]: validateBasedOnAuth(
    authTypes.BEARER,
    'Token Key is a required field',
  ),
}

// Auth: Basic
const basicAuthFields = {
  [AFF.API_AUTH_USERNAME.key]: validateBasedOnAuth(
    authTypes.BASIC,
    'Username is a required field',
  ),
  [AFF.API_AUTH_PASSWORD.key]: validateBasedOnAuth(
    authTypes.BASIC,
    'Password is a required field',
  ),
}

// Auth: API key Based
const apiKeyAuthFields = {
  [AFF.API_AUTH_API_KEY.key]: validateBasedOnAuth(
    authTypes.API_KEY,
    'API Key is a required field',
  ),
  [AFF.API_AUTH_API_VALUE.key]: validateBasedOnAuth(
    authTypes.API_KEY,
    'API Value is a required field',
  ),
}

const mobileConfigSchema = yup.object().shape({
  [AFF.ORDER_TYPES.key]: yup.array().of(yup.string()).required(),
  [AFF.POS_PAYMENTS_MODE.key]: yup
    .array()
    .of(yup.string())
    .required()
    .min(1, 'Minimum 1 payment mode is required'),
  [AFF.BARCODE_REGEX.key]: yup.string(),

  [AFF.REQUIRE_PHONE.key]: yup.boolean().default(false),
  [AFF.REQUIRE_ADDRESS.key]: yup.boolean().default(false),
  [AFF.REQUIRE_CUSTOMER_NAME.key]: yup.boolean().default(false),

  [AFF.DELIVERY_FEE_AMOUNT.key]: yup
    .number()
    .typeError('Delivery fee must be number')
    .min(0, 'Delivery fee must be non-negative')
    .when('isDeliveryFeeEnable', {
      is: true,
      then: (schema) => schema.required('Delivery fee is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
})

// Aggregator API Configuration Schema
const apiConfigurationSchema = yup.object().shape({
  // Common
  [AFF.API_ACTIVE.key]: yup.boolean(),
  [AFF.API_URL.key]: makeConditionallyRequired(
    AFF.API_ACTIVE.key,
    'API Path is a required field',
  ),
  [AFF.API_STORES.key]: yup
    .array()
    .of(yup.string())
    .when(AFF.API_ACTIVE.key, {
      is: true,
      then: (schema) =>
        schema
          .min(1, 'Please Select at least one store.')
          .required('API stores is a required field'),
      otherwise: (schema) => schema,
    }),
  [AFF.API_AUTH_TYPE.key]: yup.string(),
  [AFF.API_AUTH_TOKEN_EXPIRY.key]: yup
    .number()
    .typeError('Auth Token Expiry must be a number')
    .integer('Auth Token Expiry must be a whole number')
    .min(1, 'Minimum value allowed is 1')
    .notRequired(),
  [AFF.API_MAX_CONCURRENT_STORES.key]: yup
    .number()
    .typeError('Max Concurrent Stores must be a number')
    .integer('Max Concurrent Stores must be a whole number')
    .min(1, 'Minimum value allowed is 1')
    .when(AFF.API_ACTIVE.key, {
      is: true,
      then: (schema) => schema.required('Max Concurrent Stores is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  [AFF.API_ITEMS_PER_REQUEST.key]: yup
    .number()
    .typeError('Items Per Request must be a number')
    .integer('Items Per Request must be a whole number')
    .min(1, 'Minimum value allowed is 1')
    .max(100000, 'Maximum value allowed is 1,00,000')
    .when(AFF.API_ACTIVE.key, {
      is: true,
      then: (schema) => schema.required('Items Per Request is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  [AFF.API_MAX_PARALLEL_ITEM_BATCHES.key]: yup
    .number()
    .typeError('Max Parallel Item Batches must be a number')
    .integer('Max Parallel Item Batches must be a whole number')
    .min(1, 'Minimum value allowed is 1')
    .when(AFF.API_ACTIVE.key, {
      is: true,
      then: (schema) =>
        schema.required('Max Parallel Item Batches is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  [AFF.API_ROOT_SCHEMA.key]: makeConditionallyRequired(
    AFF.API_ACTIVE.key,
    'Root Schema is a required field',
  ).isValidJson('Please Provide Valid Root Schema', {
    excludeEmptyString: true,
  }),
  [AFF.API_PRODUCT_SCHEMA.key]: makeConditionallyRequired(
    AFF.API_ACTIVE.key,
    'Product Schema is a required field',
  ).isValidJson('Please Provide Valid Product Schema', {
    excludeEmptyString: true,
  }),

  ...bearerAuthFields,
  ...basicAuthFields,
  ...apiKeyAuthFields,
})

// Aggregator SAP Configuration Schema
const sapAggregatorConfigSchema = yup.object().shape({
  [AFF.SAP_DOC_TYPE.key]: yup.string().trim(),
  [AFF.SAP_RMA_DOC_TYPE.key]: yup.string().trim(),
  [AFF.SAP_SALES_ORG.key]: yup.string().trim(),
  [AFF.SAP_DISTR_CHANNEL.key]: yup.string().trim(),
  [AFF.SAP_DIVISION.key]: yup.string().trim(),
  [AFF.SAP_CUSTOMER_CODE.key]: yup.string().trim(),
  [AFF.SAP_PARTNERS_LIST.key]: yup
    .string()
    .trim()
    .isValidJson('Please Provide Valid JSON', {
      excludeEmptyString: true,
    }),
  [AFF.SAP_SHOULD_SEND_CREDIT_SALE.key]: yup.boolean().default(false),
})

const deliverooStringSchema = (title: string) => {
  return yup
    .string()
    .trim()
    .when('$isDeliveroo', {
      is: true,
      then: (schema) => schema.required(`${title} is a required field`),
      otherwise: (schema) => schema.notRequired(),
    })
}
const IMAGE_URL_REGEX = /^(https?:\/\/.*\.(?:png|jpg|jpeg|gif|webp))$/i
const deliverooConfigSchema = yup.object().shape({
  [DF.IS_ENABLE.key]: yup.boolean(),
  [DF.BRAND_ID.key]: deliverooStringSchema(DF.BRAND_ID.label),
  [DF.CATALOGUE_ID.key]: deliverooStringSchema(DF.CATALOGUE_ID.label),
  [DF.WEBHOOK_SECRET.key]: deliverooStringSchema(DF.WEBHOOK_SECRET.label),
  [DF.BASE_URL.key]: deliverooStringSchema(DF.BASE_URL.label),
  [DF.AUTH_BASE_URL.key]: deliverooStringSchema(DF.AUTH_BASE_URL.label),
  [DF.USERNAME.key]: deliverooStringSchema(DF.USERNAME.label),
  [DF.PASSWORD.key]: deliverooStringSchema(DF.PASSWORD.label),
  [DF.DEFAULT_PRICE_STORE_ID.key]: deliverooStringSchema(
    DF.DEFAULT_PRICE_STORE_ID.label,
  ),
  [DF.HERO_IMAGE_URL.key]: deliverooStringSchema(
    DF.HERO_IMAGE_URL.label,
  ).matches(IMAGE_URL_REGEX, {
    message: 'Invalid image URL (use png, jpg, jpeg, gif, webp)',
    excludeEmptyString: true,
  }),
})

export const createAggregatorSchema = yup.object().shape({
  // Aggregator
  [AFF.AGG_NAME.key]: yup
    .string()
    .trim()
    .required('Aggregator Name is required field')
    .matches(NO_SPECIAL_CHAR, 'Please Enter Valid Aggregator Name.'),
  [AFF.AGG_ID.key]: yup
    .string()
    .trim()
    .required('Aggregator ID is required field')
    .test('last-char-check', 'Please Enter Valid Aggregator ID.', (value) =>
      checkLastChar(value),
    )
    .matches(NO_SPECIAL_CHAR, 'Please Enter Valid Aggregator ID'),
  [AFF.AGG_IS_ACTIVE.key]: yup.boolean(),
  [AFF.AGG_GRUBTECH_ID.key]: yup.string().optional().nullable(),
  [AFF.AUTO_ATTACH_BARCODES.key]: yup
    .array()
    .of(yup.string().trim().min(1))
    .optional()
    .nullable()
    .default([]),

  // FTP Configurations
  [aggParentFields.FTP]: ftpConfigSchema,

  // Aggregator API
  [aggParentFields.API]: apiConfigurationSchema,

  // Aggregator Sap
  [aggParentFields.SAP]: sapAggregatorConfigSchema,

  // Deliveroo Configurations
  [aggParentFields.DELIVEROO]: deliverooConfigSchema,
})

export const updateAggregatorSchema = createAggregatorSchema.concat(
  yup.object().shape({
    [aggParentFields.MOBILE]: mobileConfigSchema,
  }),
)

export type CreateAggregatorType = yup.InferType<typeof createAggregatorSchema>

export type UpdateAggregatorType = yup.InferType<typeof updateAggregatorSchema>
